<template>
  <footer class="cyber-footer">

    <!-- Canvas World Map (real d3-geo projection) -->
    <div class="map-backdrop">
      <canvas ref="mapCanvas" class="map-canvas" />
    </div>

    <!-- Gradient overlay for readability -->
    <div class="map-overlay"></div>

    <!-- Wave Top -->
    <div class="wave-top" aria-hidden="true">
      <svg viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0,40 C180,80 360,0 540,40 C720,80 900,0 1080,40 C1260,80 1350,20 1440,40 L1440,0 L0,0 Z" fill="#1e40af"/>
      </svg>
    </div>

    <!-- Footer Content -->
    <div class="footer-content">
      <div class="container">

        <!-- Top Bar -->
        <div class="top-bar">
          <div class="brand-block">
              <img src="/favicon.png" alt="Cyber Security" class="w-10 h-10 object-contain" />
            <div>
              <div class="brand-name text-left">ANTIC / CIRT</div>
              <div class="brand-sub text-left">{{ $t('footer.brandSub') }}</div>
            </div>
          </div>
          <div class="status-badge">
            <span class="status-dot"></span>
            <span>{{ $t('footer.systemOperational') }}</span>
          </div>
        </div>

        <!-- Main Grid -->
        <div class="main-grid">

          <!-- Col 1: About -->
          <div class="col-about">
            <h3 class="col-title text-left">{{ $t('footer.about.title') }}</h3>
            <p class="col-desc text-left">{{ $t('footer.about.desc') }}</p>
            <div class="security-bar">
              <div class="sec-bar-header">
                <span class="enc-label">{{ $t('footer.about.encryptionLabel') }}</span>
                <span class="enc-value">AES-256</span>
              </div>
              <div class="bar-track"><div class="bar-fill"></div></div>
            </div>
            <div class="social-row">
              <a href="https://www.facebook.com/ANTIC237" target="_blank" rel="noopener" class="social-btn" aria-label="Facebook">
                <svg class="icon-sm" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
              <a href="https://x.com/antic_cameroun" target="_blank" rel="noopener" class="social-btn" aria-label="Twitter">
                <svg class="icon-sm" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
              </a>
              <a href="https://www.linkedin.com/company/antic/" target="_blank" rel="noopener" class="social-btn" aria-label="LinkedIn">
                <svg class="icon-sm" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="https://www.antic.cm/index.php/" target="_blank" rel="noopener" class="social-btn" aria-label="Website">
                <svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"/></svg>
              </a>
            </div>
          </div>

          <!-- Col 2: Resources -->
          <div class="col-links">
            <h3 class="col-title">{{ $t('footer.resources.title') }}</h3>
            <ul class="link-list">
              <li><a href="https://www.antic.cm/index.php/" target="_blank" rel="noopener" class="link-item"><span class="link-dot"></span><span>{{ $t('footer.resources.antic') }}</span><svg class="link-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg></a></li>
              <li><a href="https://cirt.cm/" target="_blank" rel="noopener" class="link-item"><span class="link-dot"></span><span>{{ $t('footer.resources.cirt') }}</span><svg class="link-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg></a></li>
              <li><a href="#" class="link-item"><span class="link-dot"></span><span>{{ $t('footer.resources.guidelines') }}</span><svg class="link-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg></a></li>
              <li><a href="#" class="link-item"><span class="link-dot"></span><span>{{ $t('footer.resources.threats') }}</span><svg class="link-chevron" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg></a></li>
            </ul>
          </div>

          <!-- Col 3: Contact -->
<div class="col-contact">
  <h3 class="col-title text-left">{{ $t('footer.contact.title') }}</h3>
  <div class="contact-list">
    <div class="contact-row">
      <div class="contact-icon-wrap"><svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg></div>
      <div class="text-left">
        <p class="contact-label text-left font-bold">{{ $t('footer.contact.emailLabel') }}</p>
        <a href="mailto:alerts@cirt.cm" class="contact-value text-left block font-bold">alerts@cirt.cm</a>
      </div>
    </div>
    <div class="contact-row">
      <div class="contact-icon-wrap"><svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg></div>
      <div class="text-left">
        <p class="contact-label text-left font-bold">{{ $t('footer.contact.hotlineLabel') }}</p>
        <a href="tel:8202" class="contact-value text-left block font-bold">8202</a>
      </div>
    </div>
    <div class="contact-row">
      <div class="contact-icon-wrap"><svg class="icon-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg></div>
      <div class="text-left">
        <p class="contact-label text-left font-bold">{{ $t('footer.contact.locationLabel') }}</p>
        <p class="contact-value text-left font-bold">{{ $t('footer.contact.locationValue') }}</p>
      </div>
    </div>
  </div>
</div>

        </div>

        <!-- Bottom Bar -->
        <div class="bottom-bar">
          <p class="copyright">© 2025 ANTIC/CIRT — {{ $t('footer.copyright') }}</p>
          <div class="legal-links">
            <a href="#" class="legal-link">{{ $t('footer.privacy') }}</a>
            <span class="divider">·</span>
            <a href="#" class="legal-link">{{ $t('footer.terms') }}</a>
          </div>
        </div>

      </div>
    </div>
  </footer>
</template>

<script>
import { geoNaturalEarth1, geoPath, geoGraticule } from 'd3-geo'
import { feature, mesh } from 'topojson-client'

// Cameroon numeric ISO-3166 id in world-atlas
const CAMEROON_ID = 120

export default {
  name: 'CyberFooter',

  data() {
    return {
      worldGeo: null,
      animFrame: null,
      resizeObserver: null,
      canvasW: 0,
      canvasH: 0,
    }
  },

  async mounted() {
    await this.loadGeo()
    this.observeSize()
  },

  beforeUnmount() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame)
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },

  methods: {
    // ── 1. Fetch real world TopoJSON (same CDN as React version) ──
    async loadGeo() {
      try {
        const res  = await fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json')
        const topo = await res.json()

        const countriesCollection = feature(topo, topo.objects.countries)
        const cameroon = countriesCollection.features.find(f => +f.id === CAMEROON_ID)
        const borders  = mesh(topo, topo.objects.countries, (a, b) => a !== b)

        this.worldGeo = {
          countries: countriesCollection.features,
          cameroon,
          borders,
        }

        // trigger first render once data is ready
        this.startRender()
      } catch (err) {
        // world map load failed
      }
    },

    // ── 2. ResizeObserver keeps canvas filling the footer ─────────
    observeSize() {
      this.resizeObserver = new ResizeObserver(() => {
        const el = this.$el
        if (!el) return
        this.canvasW = el.offsetWidth
        this.canvasH = el.offsetHeight
        this.startRender()
      })
      this.resizeObserver.observe(this.$el)
      this.canvasW = this.$el.offsetWidth
      this.canvasH = this.$el.offsetHeight
    },

    // ── 3. Build projection (NaturalEarth, same as React file) ────
    makeProj(w, h) {
      return geoNaturalEarth1()
        .scale(w / 5.2)
        .translate([w * 0.48, h * 0.54])
    },

    // ── 4. Animated render loop ───────────────────────────────────
    startRender() {
      if (this.animFrame) cancelAnimationFrame(this.animFrame)
      if (!this.worldGeo || !this.canvasW) return

      const canvas = this.$refs.mapCanvas
      if (!canvas) return

      const dpr = window.devicePixelRatio || 1
      const W   = this.canvasW
      const H   = this.canvasH

      canvas.width  = W * dpr
      canvas.height = H * dpr

      const ctx     = canvas.getContext('2d')
      ctx.scale(dpr, dpr)

      const proj    = this.makeProj(W, H)
      const pathGen = geoPath(proj, ctx)
      const grat    = geoGraticule().step([20, 20])()

      const render = () => {
        ctx.clearRect(0, 0, W, H)

        this.drawGraticule(ctx, pathGen, grat)
        this.drawCountries(ctx, pathGen)
        this.drawCameroon(ctx, pathGen)

        this.animFrame = requestAnimationFrame(render)
      }

      render()
    },

    // ── 5. Draw helpers (mirroring React ThreatMap helpers) ───────
    drawGraticule(ctx, pathGen, grat) {
      ctx.beginPath()
      pathGen(grat)
      ctx.strokeStyle = 'rgba(0, 150, 255, 0.07)'
      ctx.lineWidth   = 0.5
      ctx.stroke()
    },

    drawCountries(ctx, pathGen) {
      const { countries, borders } = this.worldGeo

      countries.forEach(country => {
        ctx.beginPath()
        pathGen(country)
        ctx.fillStyle = 'rgba(15, 35, 65, 0.75)'
        ctx.fill()
      })

      ctx.beginPath()
      pathGen(borders)
      ctx.strokeStyle = 'rgba(0, 150, 255, 0.15)'
      ctx.lineWidth   = 0.5
      ctx.stroke()
    },

    drawCameroon(ctx, pathGen) {
      const { cameroon } = this.worldGeo
      if (!cameroon) return

      const centroid = pathGen.centroid(cameroon)

      // Ambient glow
      if (centroid?.[0]) {
        const g0 = ctx.createRadialGradient(
          centroid[0], centroid[1], 0,
          centroid[0], centroid[1], 380
        )
        g0.addColorStop(0,   'rgba(251, 191, 36, 0.10)')
        g0.addColorStop(0.4, 'rgba(251, 191, 36, 0.03)')
        g0.addColorStop(1,   'rgba(251, 191, 36, 0)')
        ctx.fillStyle = g0
        ctx.beginPath()
        ctx.arc(centroid[0], centroid[1], 380, 0, Math.PI * 2)
        ctx.fill()
      }

      // Country fill
      ctx.beginPath()
      pathGen(cameroon)
      ctx.fillStyle = 'rgba(251, 191, 36, 0.28)'
      ctx.fill()

      // Soft glow border
      ctx.beginPath()
      pathGen(cameroon)
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.18)'
      ctx.lineWidth   = 8
      ctx.stroke()

      // Crisp border
      ctx.beginPath()
      pathGen(cameroon)
      ctx.strokeStyle = 'rgba(251, 191, 36, 0.9)'
      ctx.lineWidth   = 1.5
      ctx.stroke()

      // Pulse rings + dot
      if (centroid?.[0]) {
        const t      = Date.now()
        const p1     = (t % 3500) / 3500
        const p2     = ((t + 1750) % 3500) / 3500

        ctx.strokeStyle = `rgba(251,191,36,${(0.35 - p1 * 0.35).toFixed(3)})`
        ctx.lineWidth   = 1.5
        ctx.beginPath()
        ctx.arc(centroid[0], centroid[1], 10 + p1 * 90, 0, Math.PI * 2)
        ctx.stroke()

        ctx.strokeStyle = `rgba(251,191,36,${(0.22 - p2 * 0.22).toFixed(3)})`
        ctx.lineWidth   = 1
        ctx.beginPath()
        ctx.arc(centroid[0], centroid[1], 10 + p2 * 90, 0, Math.PI * 2)
        ctx.stroke()

        // Centre dot
        ctx.shadowColor = '#fbbf24'
        ctx.shadowBlur  = 14
        ctx.fillStyle   = '#fbbf24'
        ctx.beginPath()
        ctx.arc(centroid[0], centroid[1], 4, 0, Math.PI * 2)
        ctx.fill()
        ctx.shadowBlur  = 0

        // Label
        ctx.fillStyle  = 'rgba(253, 230, 138, 0.92)'
        ctx.font       = 'bold 11px "Segoe UI", sans-serif'
        ctx.textAlign  = 'left'
        ctx.fillText('', centroid[0] + 9, centroid[1] - 7)
        ctx.textAlign  = 'start'
      }
    },
  },
}
</script>

<style scoped>
.cyber-footer {
  position: relative;
  background: #0a0f1e;
  overflow: hidden;
  font-family: 'Segoe UI', system-ui, sans-serif;
}

/* ─── Canvas Map ─── */
.map-backdrop {
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.map-canvas {
  width: 100%;
  height: 100%;
  display: block;
}

/* ─── Overlay ─── */
.map-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(10,15,30,0.82) 0%,
    rgba(10,15,30,0.52) 50%,
    rgba(10,15,30,0.85) 100%
  );
  pointer-events: none;
}

/* ─── Wave ─── */
.wave-top { position: relative; z-index: 1; line-height: 0; transform: rotate(180deg); }
.wave-top svg { width: 100%; height: 50px; display: block; }

/* ─── Content ─── */
.footer-content { position: relative; z-index: 2; padding: 3rem 0 0; }
.container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }

/* ─── Top Bar ─── */
.top-bar {
  display: flex; align-items: center; justify-content: space-between;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(59,130,246,0.2);
  margin-bottom: 2.5rem;
  flex-wrap: wrap; gap: 1rem;
}
.brand-block { display: flex; align-items: center; gap: 0.875rem; }
.brand-icon {
  width: 48px; height: 48px;
  background: linear-gradient(135deg, #1d4ed8, #3b82f6);
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 0 20px rgba(59,130,246,0.4); flex-shrink: 0;
}
.icon-lg { width: 28px; height: 28px; color: #fff; }
.icon-sm { width: 16px; height: 16px; }
.brand-name { font-size: 1.25rem; font-weight: 800; color: #fff; letter-spacing: 0.05em; }
.brand-sub  { font-size: 0.68rem; color: #64748b; letter-spacing: 0.08em; text-transform: uppercase; margin-top: 2px; }

.status-badge {
  display: inline-flex; align-items: center; gap: 0.5rem;
  background: rgba(16,185,129,0.1); border: 1px solid rgba(16,185,129,0.25);
  border-radius: 999px; padding: 0.375rem 0.875rem;
  font-size: 0.75rem; font-weight: 600; color: #34d399; letter-spacing: 0.04em;
}
.status-dot {
  width: 7px; height: 7px; background: #10b981;
  border-radius: 50%; flex-shrink: 0;
  animation: status-pulse 2s ease-in-out infinite;
}
@keyframes status-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); }
  50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
}

/* ─── Grid ─── */
.main-grid { display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 3rem; padding-bottom: 2.5rem; }
@media (max-width: 900px) { .main-grid { grid-template-columns: 1fr 1fr; gap: 2rem; } .col-about { grid-column: 1/-1; } }
@media (max-width: 560px) { .main-grid { grid-template-columns: 1fr; gap: 2rem; } }

/* ─── Col titles ─── */
.col-title {
  font-size: 0.68rem; font-weight: 700; letter-spacing: 0.12em;
  text-transform: uppercase; color: #3b82f6; margin-bottom: 1.25rem;
  display: flex; align-items: center; gap: 0.5rem;
}
.col-title::before { content:''; display:inline-block; width:18px; height:2px; background:#3b82f6; border-radius:2px; }

/* ─── About ─── */
.col-desc { font-size: 0.875rem; color: #94a3b8; line-height: 1.7; margin-bottom: 1.25rem; }
.security-bar {
  background: rgba(15,23,42,0.8); border: 1px solid rgba(59,130,246,0.15);
  border-radius: 10px; padding: 0.875rem 1rem; margin-bottom: 1.25rem;
}
.sec-bar-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
.enc-label { font-size: 0.65rem; font-weight: 700; color: #34d399; text-transform: uppercase; letter-spacing: 0.08em; }
.enc-value { font-size: 0.7rem; color: #64748b; }
.bar-track { height: 4px; background: rgba(255,255,255,0.07); border-radius: 99px; overflow: hidden; }
.bar-fill  { height: 100%; width: 100%; background: linear-gradient(90deg, #10b981, #34d399); border-radius: 99px; }

.social-row { display: flex; gap: 0.625rem; }
.social-btn {
  width: 36px; height: 36px; border-radius: 8px;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.08);
  display: inline-flex; align-items: center; justify-content: center;
  color: #94a3b8; transition: all 0.2s; text-decoration: none;
}
.social-btn:hover { background: #1d4ed8; border-color: #3b82f6; color: #fff; transform: translateY(-2px); box-shadow: 0 6px 16px rgba(59,130,246,0.3); }

/* ─── Links ─── */
.link-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.25rem; }
.link-item {
  display: flex; align-items: center; gap: 0.625rem;
  color: #94a3b8; text-decoration: none; font-size: 0.875rem;
  padding: 0.5rem 0; border-bottom: 1px solid rgba(255,255,255,0.04); transition: color 0.2s;
}
.link-item:hover { color: #e2e8f0; }
.link-item:hover .link-chevron { opacity: 1; transform: translateX(3px); }
.link-dot { width: 5px; height: 5px; border-radius: 50%; background: #3b82f6; flex-shrink: 0; }
.link-chevron { width: 12px; height: 12px; margin-left: auto; opacity: 0.3; transition: all 0.2s; flex-shrink: 0; }

/* ─── Contact ─── */
.contact-list { display: flex; flex-direction: column; gap: 1rem; }
.contact-row  { display: flex; align-items: flex-start; gap: 0.75rem; }
.contact-icon-wrap {
  width: 32px; height: 32px;
  background: rgba(59,130,246,0.12); border: 1px solid rgba(59,130,246,0.2);
  border-radius: 8px; display: flex; align-items: center; justify-content: center;
  color: #60a5fa; flex-shrink: 0; margin-top: 2px;
}
.contact-label { font-size: 0.68rem; color: #475569; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 2px; }
.contact-value { font-size: 0.875rem; color: #cbd5e1; text-decoration: none; transition: color 0.2s; line-height: 1.4; }
a.contact-value:hover { color: #60a5fa; }

/* ─── Bottom ─── */
.bottom-bar {
  border-top: 1px solid rgba(255,255,255,0.06); padding: 1.5rem 0;
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem;
}
.copyright  { font-size: 0.75rem; color: #475569; }
.legal-links { display: flex; align-items: center; gap: 0.625rem; }
.legal-link  { font-size: 0.75rem; color: #475569; text-decoration: none; transition: color 0.2s; }
.legal-link:hover { color: #94a3b8; }
.divider    { color: #334155; }

@media (prefers-reduced-motion: reduce) {
  .status-dot { animation: none; }
  .social-btn, .link-item { transition: none; }
}
</style>